import { MonitoringPromptRunStatus, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { DEFAULT_SOURCE_GROUPING, foldHost, SourceGrouping } from './Components/citationSources.utils';
import { IToneBearingRow, TONE_METRIC, ToneFamily, ToneMetricName, toneValue } from './toneMetric';

/**
 * Pure, dependency-light helpers for the "AI answers" Analytics sub-tab.
 *
 * Everything here is deliberately free of React and the REST client so it can be unit
 * tested in isolation and reused by both the list screen and the answer-detail screen.
 */

/**
 * Facets that represent the customer's own presence ("You"). A run can surface several
 * of these (multiple brands / products); the highest-share own entity is treated as the
 * representative "You" in the mentions column.
 */
export const OWN_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Brand,
  TaxonomyFacet.Product,
  TaxonomyFacet.Program,
  TaxonomyFacet.Site,
  TaxonomyFacet.Initiative,
];

/** Facets that represent rivals. */
export const COMPETITOR_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Competitor,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.CompetitorProgram,
];

/**
 * Facets shown in the mentions column: Brands, Competitors, Products, Competitor Products,
 * Programs, Competitor Programs.
 */
export const VISIBLE_MENTION_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Brand,
  TaxonomyFacet.Competitor,
  TaxonomyFacet.Product,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.Program,
  TaxonomyFacet.CompetitorProgram,
];

/** The shape `targetPositionInAnswer` reads off a run's entity list. */
export interface AnswerPositionEntity {
  type?: TaxonomyFacet;
  /** 1-based sequence of the entity's EARLIEST mention in this answer; absent when unmentioned. */
  order?: number | null;
}

/**
 * Where the customer's own subject came in THIS answer, or undefined when the answer never
 * positioned it.
 *
 * The figure is an integer at answer grain — a placing in one answer's mention sequence, not the
 * mean that the visibility table ranks subjects by — so it carries no hover and no rank arithmetic.
 *
 * ABSENT IS ABSENT. An answer that never mentions the target has no position to report, and the
 * caller says so; `0` would read as a placing ahead of first, and any fallback would put the target
 * in a sequence it never entered. Position 1 is a real, common value, so a falsy check would
 * discard it — the guard is finiteness and a floor of 1, not truthiness.
 *
 * `order` is already the earliest mention per entity (an answer naming a subject twice reports it
 * once), so the earliest own subject is simply the smallest order among own entities — which is
 * what a customer tracking a brand plus several of its products should see as "your position".
 */
export function targetPositionInAnswer(entities: readonly AnswerPositionEntity[] | undefined): number | undefined {
  const ownPositions = (entities ?? [])
    .filter((entity) => classifyEntityOwnership(entity.type) === 'own')
    .map((entity) => entity.order)
    .filter((order): order is number => typeof order === 'number' && Number.isFinite(order) && order >= 1);

  return ownPositions.length ? Math.min(...ownPositions) : undefined;
}

export type EntityOwnership = 'own' | 'competitor' | 'other';

export function classifyEntityOwnership(facet?: TaxonomyFacet): EntityOwnership {
  if (facet && OWN_FACETS.includes(facet)) {
    return 'own';
  }

  if (facet && COMPETITOR_FACETS.includes(facet)) {
    return 'competitor';
  }

  return 'other';
}

export type SentimentTone = 'excellent' | 'good' | 'neutral' | 'negative' | 'veryNegative';

/** Where a metric's display bands begin and end, on that metric's own 0–1 scale. */
export interface ToneBounds {
  /**
   * Below this the metric reads Very negative. Only a scale with a fifth band sets it; on a scale
   * without one, Negative is the bottom band and nothing reads Very negative.
   */
  veryNegativeBelow?: number;
  /** Below this (and not Very negative) the metric reads Negative: the text itself reads unfavourably. */
  negativeBelow: number;
  /** At or above this the metric reads Good. Below it (but not negative) it reads Neutral. */
  goodAtOrAbove: number;
  /** At or above this the metric reads Excellent. */
  excellentAtOrAbove: number;
}

/**
 * The absolute floor below which a score is called Negative, on the sentiment and attribute scales.
 *
 * Deliberately not a distribution cut point — the other edges on those scales say where a score
 * sits among its peers, this one says the text reads unfavourably. Only a score under it earns a
 * thumbs-down, so the indicator can never contradict an answer a reader can see is friendly.
 */
export const ABSOLUTE_NEGATIVE_BELOW = 0.45;

/**
 * Display bands for ENTITY SENTIMENT and for ATTRIBUTE SCORE, from the 2026-08-30 stage
 * distribution.
 *
 * AI answers skew favourable, so the bands are positioned by where scores actually PILE UP rather
 * than by even thirds. Roughly, with each band carrying a meaningful share of real answers:
 *
 *   entity sentiment    negative ~1-2%   neutral ~32%   good ~48%   excellent ~18%
 *   attribute score     negative ~11%    neutral ~22%   good ~43%   excellent ~24%
 *
 * Entity `excellent` starts at 0.70, about the 82nd percentile — high enough that it stays a
 * genuine standout rather than the default reading.
 *
 * The metrics do not share edges because they are not distributed alike. The attribute Negative
 * pile is far larger (~11% against ~1-2%) and that is REAL, not noise: attribute scores include
 * deterministic conversions of rating cells, which produce genuine lows that sentiment prose
 * rarely reaches. The thumbs-down stays text-backed for both — an attribute under 0.45 really does read
 * unfavourably, so the larger pile is a truer picture rather than a reason to move the floor.
 *
 * Re-measure and update both together if the scoring model changes. Do not nudge a single edge to
 * make one answer read better.
 */
export const ENTITY_SENTIMENT_TONE_BOUNDS: ToneBounds = {
  negativeBelow: ABSOLUTE_NEGATIVE_BELOW,
  goodAtOrAbove: 0.58,
  excellentAtOrAbove: 0.7,
};
export const ATTRIBUTE_SCORE_TONE_BOUNDS: ToneBounds = {
  negativeBelow: ABSOLUTE_NEGATIVE_BELOW,
  goodAtOrAbove: 0.54,
  excellentAtOrAbove: 0.75,
};

/**
 * Display bands for the VERDICT: five, one per grade of its rubric, and fitted to nothing.
 *
 * The verdict score is the share-weighted mean grade rescaled onto 0–1, so every grade has a fixed
 * place on the scale: argues against at 0, raises a caveat at 0.25, descriptive or balanced at 0.5,
 * favourable at 0.75, recommends without qualification at 1. Each boundary sits halfway between two
 * neighbouring grades, so a reading lands in the band of the grade its average is closest to, and a
 * subject judged at one grade throughout shows exactly that grade's band.
 *
 * The sentiment bands above are positioned by where sentiment scores pile up, because a sentiment
 * score reads only against how AI answers usually score. A verdict grade already says what the
 * answer did, so the bands need no distribution behind them: they are symmetric around the
 * descriptive middle, the three inner bands equally wide and the two outer ones half that.
 *
 * An exact tie reads the kinder band, as on every scale here: half descriptive and half favourable
 * is 0.625, and reads Good.
 */
export const VERDICT_TONE_BOUNDS: ToneBounds = {
  veryNegativeBelow: 0.125,
  negativeBelow: 0.375,
  goodAtOrAbove: 0.625,
  excellentAtOrAbove: 0.875,
};

/**
 * The bands each measure of tone is read against — the one place a measure is paired with its bands,
 * so whichever measure `TONE_METRIC` selects arrives with its own.
 */
export const TONE_METRIC_BOUNDS: Readonly<Record<ToneMetricName, ToneBounds>> = {
  sentiment: ENTITY_SENTIMENT_TONE_BOUNDS,
  verdict: VERDICT_TONE_BOUNDS,
};

/** What each band is called, for tooltips and legends. */
export const SENTIMENT_TONE_LABELS: Readonly<Record<SentimentTone, string>> = {
  excellent: 'Excellent',
  good: 'Good',
  neutral: 'Neutral',
  negative: 'Negative',
  veryNegative: 'Very negative',
};

/**
 * The explanation each band needs on the sentiment and attribute scales.
 *
 * Neutral carries the most weight: AI answers skew favourable, so a neutral score is not a neutral
 * answer — it is one that favours you LESS than answers typically do. Without saying that, a
 * reader takes Neutral for "nothing to see here" when it is the band worth acting on.
 */
export const SENTIMENT_TONE_HINTS: Readonly<Record<SentimentTone, string>> = {
  excellent: 'Among the most favourable answers — a clear standout.',
  good: 'Favourable, in line with how AI answers usually read.',
  neutral: 'AI answers skew favourable, so this means the answer does not favour you as much as a typical answer does.',
  negative: 'The text itself reads unfavourably.',
  // Neither scale has a band below Negative, so nothing on them reads Very negative; the entry keeps
  // the map total and says what their bottom band says.
  veryNegative: 'The text itself reads unfavourably.',
};

/**
 * The explanation each band needs on the verdict scale: the grade the answers' average lands on.
 *
 * None of the sentiment wording carries over. There a band says where an answer sits among other
 * answers; here it says what the answers did, so Neutral means descriptive or balanced rather than
 * "less favourable than usual".
 */
export const VERDICT_TONE_HINTS: Readonly<Record<SentimentTone, string>> = {
  excellent: 'On average the answers recommend it without qualification.',
  good: 'On average the answers speak favourably of it.',
  neutral: 'On average the answers are descriptive or balanced, neither for nor against it.',
  negative: 'On average the answers raise caveats about it.',
  veryNegative: 'On average the answers argue against it.',
};

/**
 * Normalizes a metric onto its 0–1 scale. Accepts either a score or an already-scaled percent;
 * values within [-1, 1] are taken as the score they already are.
 */
function toneScore(value?: number | null): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }

  return value >= -1 && value <= 1 ? value : value / 100;
}

/** The percentage form of a 0–1 score, for display. */
export function sentimentPercent(value?: number | null): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }

  if (value >= -1 && value <= 1) {
    return value * 100;
  }

  return value;
}

/** Places a value in its metric's bands. Classification happens on the score, never on a percent. */
export function toneForScore(value: number | null | undefined, bounds: ToneBounds): SentimentTone | undefined {
  const score = toneScore(value);
  if (score === undefined) {
    return undefined;
  }

  // Ordered from the harshest band upward, so each test runs only once every harsher band has been
  // ruled out. A scale that sets no Very negative floor never reaches that band.
  if (bounds.veryNegativeBelow !== undefined && score < bounds.veryNegativeBelow) {
    return 'veryNegative';
  }

  if (score < bounds.negativeBelow) {
    return 'negative';
  }

  if (score >= bounds.excellentAtOrAbove) {
    return 'excellent';
  }

  if (score >= bounds.goodAtOrAbove) {
    return 'good';
  }

  return 'neutral';
}

/**
 * How a row's tone reads — its reading in the measure `TONE_METRIC` selects, placed on that measure's
 * own bands.
 *
 * The one classification behind every tone thumb on a subject, so the answer list and the answer
 * page it opens can never read the same subject in two measures, or one measure on another's bands.
 */
export function toneForRow(row: IToneBearingRow | null | undefined, family?: ToneFamily): SentimentTone | undefined {
  return toneForScore(toneValue(row, family), TONE_METRIC_BOUNDS[TONE_METRIC]);
}

/** Tailwind text-color class for a sentiment tone, standardized across screens. */
export function sentimentToneColorClass(tone?: SentimentTone): string {
  if (tone === 'excellent' || tone === 'good') {
    return 'text-emerald-600';
  }

  if (tone === 'negative' || tone === 'veryNegative') {
    return 'text-rose-600';
  }

  // Neutral is grey, never amber or red: on no scale is it a warning about the text.
  return 'text-slate-400';
}

/**
 * A minimal view of a run entity as needed by the mentions column. Its tone reading arrives in both
 * measures, and the badge reads the one the switch selects, as the answer page does.
 */
export interface AiAnswerEntityLike extends IToneBearingRow {
  name: string;
  type: TaxonomyFacet;
  shareOfVoice: number;
  recommendation?: number;
  /**
   * The taxonomy terms this mention was credited to. The run payload carries references, not full
   * terms, so a badge cannot know the term's curation status from this alone — it carries the key
   * forward and the rendering component resolves the status through the shared term cache.
   */
  terms?: readonly unknown[];
}

/** The stable key of the term a mention was credited to, when it was credited to one. */
export function mentionTermKey(entity: AiAnswerEntityLike): string | undefined {
  for (const reference of entity.terms ?? []) {
    if (!reference || typeof reference !== 'object') {
      continue;
    }
    const record = reference as { key?: unknown; getStableKey?: () => string };
    const key = String(record.getStableKey?.() ?? record.key ?? '').trim();
    if (key) {
      return key;
    }
  }
  return undefined;
}

export type MentionBadge =
  | {
      kind: 'entity';
      name: string;
      ownership: EntityOwnership;
      sovPercent: number;
      sentimentTone?: SentimentTone;
      isYou: boolean;
      /** Resolved by the renderer to present the term's curation status. */
      termKey?: string;
    }
  | { kind: 'you-invisible' }
  | { kind: 'more'; count: number };

/** Share-of-voice as a percentage, clamped to a sane range. */
export function shareOfVoicePercent(value?: number | null): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }

  const percent = value >= 0 && value <= 1 ? value * 100 : value;
  return Math.max(0, Math.round(percent));
}

function toEntityBadge(entity: AiAnswerEntityLike, isYou: boolean): MentionBadge {
  return {
    kind: 'entity',
    name: entity.name,
    ownership: classifyEntityOwnership(entity.type),
    sovPercent: shareOfVoicePercent(entity.shareOfVoice),
    sentimentTone: toneForRow(entity),
    isYou,
    termKey: mentionTermKey(entity),
  };
}

/** The brands an answer mentioned with a share of voice, strongest first. */
function rankMentionedEntities(entities: readonly AiAnswerEntityLike[]): AiAnswerEntityLike[] {
  return entities
    .filter((entity) => VISIBLE_MENTION_FACETS.includes(entity.type) && shareOfVoicePercent(entity.shareOfVoice) > 0)
    .slice()
    .sort((a, b) => shareOfVoicePercent(b.shareOfVoice) - shareOfVoicePercent(a.shareOfVoice));
}

/** One brand's segment of the share bar on an answers-list row. */
export interface MentionShareSegment {
  name: string;
  sovPercent: number;
  isYou: boolean;
}

/**
 * Every brand the answer mentioned, as the segments of a row's share bar: strongest first, and chosen
 * exactly as the mention badges are, so the bar and the names under it cannot disagree. Empty for a
 * run whose analysis has not finished, which has measured nothing.
 */
export function buildMentionShareSegments(
  entities: readonly AiAnswerEntityLike[],
  options?: { status?: MonitoringPromptRunStatus }
): MentionShareSegment[] {
  if (options && 'status' in options && !isProcessedRunStatus(options.status)) {
    return [];
  }
  return rankMentionedEntities(entities).map((entity) => ({
    name: entity.name,
    sovPercent: shareOfVoicePercent(entity.shareOfVoice),
    isYou: classifyEntityOwnership(entity.type) === 'own',
  }));
}

/**
 * Builds the ordered mention badges for a run row:
 *  - top-3 mentions by share of voice, "You" colorized primary and always shown;
 *  - if You ranks 4th+, show 1st, a "+X" summary of the in-between ranks, then You;
 *  - if You is invisible, show a leading "You invisible" badge;
 *  - a trailing "+X" badge counts brands ranked after what is displayed.
 */
export function buildMentionBadges(
  entities: readonly AiAnswerEntityLike[],
  options?: { topN?: number; status?: MonitoringPromptRunStatus }
): MentionBadge[] {
  const topN = options?.topN ?? 3;

  // A run that has not finished analysis has measured nothing. Rendering badges for it — above all
  // the "You invisible" badge — asserts a measurement that never happened. When no status is given
  // the caller is working from entities alone and the entity-based rules stand.
  if (options && 'status' in options && !isProcessedRunStatus(options.status)) {
    return [];
  }

  const ranked = rankMentionedEntities(entities);

  if (!ranked.length) {
    return [{ kind: 'you-invisible' }];
  }

  const youIndex = ranked.findIndex((entity) => classifyEntityOwnership(entity.type) === 'own');
  const badges: MentionBadge[] = [];

  // You is invisible: lead with the danger badge, then the strongest others.
  if (youIndex === -1) {
    badges.push({ kind: 'you-invisible' });
    const shown = ranked.slice(0, topN);
    shown.forEach((entity) => badges.push(toEntityBadge(entity, false)));
    const remaining = ranked.length - shown.length;
    if (remaining > 0) {
      badges.push({ kind: 'more', count: remaining });
    }
    return badges;
  }

  // You is within the top N: show the top N as-is, highlighting You.
  if (youIndex < topN) {
    const shown = ranked.slice(0, topN);
    shown.forEach((entity, index) => badges.push(toEntityBadge(entity, index === youIndex)));
    const remaining = ranked.length - shown.length;
    if (remaining > 0) {
      badges.push({ kind: 'more', count: remaining });
    }
    return badges;
  }

  // You ranks 4th or later: 1st, a "+X" for the in-betweens, then You.
  badges.push(toEntityBadge(ranked[0], false));
  const inBetween = youIndex - 1;
  if (inBetween > 0) {
    badges.push({ kind: 'more', count: inBetween });
  }
  badges.push(toEntityBadge(ranked[youIndex], true));
  const after = ranked.length - 1 - youIndex;
  if (after > 0) {
    badges.push({ kind: 'more', count: after });
  }

  return badges;
}

export interface WinnerInfo {
  name: string;
  ownership: EntityOwnership;
  evidence?: string;
}

/**
 * Resolves the run's "winner" (voted explicit recommendation) synchronously from the
 * materialized fields: the per-entity `recommendation === 1` flag marks which entity the
 * recommendation canonicalizes to, and the mirrored `explicitRecommendation.evidence`
 * carries the verbatim quote — so no parsed-file download is needed.
 */
export function resolveWinner(entities: readonly AiAnswerEntityLike[] | undefined, evidence?: string): WinnerInfo | undefined {
  const winner = (entities || []).find((entity) => entity.recommendation === 1);
  if (!winner) {
    return undefined;
  }

  return {
    name: winner.name,
    ownership: classifyEntityOwnership(winner.type),
    evidence: evidence?.trim() || undefined,
  };
}

/** Relative "2h ago" style label; falls back to an absolute date for older timestamps. */
export function formatRelativeTime(epochMs?: number, nowMs?: number): string {
  if (typeof epochMs !== 'number' || !Number.isFinite(epochMs)) {
    return '-';
  }

  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  const deltaSeconds = Math.round((now - epochMs) / 1000);

  if (deltaSeconds < 45) {
    return 'just now';
  }
  if (deltaSeconds < 90) {
    return '1m ago';
  }

  const minutes = Math.round(deltaSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }

  const weeks = Math.round(days / 7);
  if (weeks < 5) {
    return `${weeks}w ago`;
  }

  return new Date(epochMs).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Quick-filter for the recommendation (winner) dimension. */
export type RecommendationFilter = 'all' | 'us' | 'competitor' | 'none';

/** Quick-filter for the customer's own visibility in the answer. */
export type VisibilityFilter = 'all' | 'visible' | 'invisible';

export interface AiAnswersFilterState {
  purposeTermKey: string;
  engineKey: string;
  localeKey: string;
  promptKey: string;
  recommendation: RecommendationFilter;
  visibility: VisibilityFilter;
  includeFailed: boolean;
  /** Which language the Prompt column shows. Default: English when the template has an English instance. */
  promptLanguage: PromptLanguageMode;
  /** Deep-link drill-down target from the Recommendation KPI / attribute evidence links. */
  recommendedTermKey: string;
  /** Deep-link drill-down target from the Attributes section (answers where it appears). */
  aspectTermKey: string;
  /** Normalized untracked co-subject name, from a suggestion's evidence link. */
  coSubjectNorm: string;
  /**
   * Drill-down to answers citing one website.
   *
   * Three separate dimensions rather than one, because they answer different questions and the
   * server matches them against different fields: an exact hostname, a whole registrable domain
   * (the target while the grouped-by-domain view is active), and a curated `authority-source`
   * term — which the server expands to every hostname its name and aliases claim, so a link built
   * from a curated row keeps working when the operator adds another alias to it.
   */
  citedHost: string;
  citedDomain: string;
  citedTermKey: string;
  /** Whether the source chips group by registrable domain or list full hostnames. */
  sourceGrouping: SourceGrouping;
  page: number;
}

export const EMPTY_AI_ANSWERS_FILTERS: AiAnswersFilterState = {
  purposeTermKey: '',
  engineKey: '',
  localeKey: '',
  promptKey: '',
  recommendation: 'all',
  visibility: 'all',
  includeFailed: false,
  promptLanguage: 'english',
  recommendedTermKey: '',
  aspectTermKey: '',
  coSubjectNorm: '',
  citedHost: '',
  citedDomain: '',
  citedTermKey: '',
  sourceGrouping: DEFAULT_SOURCE_GROUPING,
  page: 0,
};

/**
 * Filters accepted by the run-list endpoint.
 *
 * Declared here rather than imported so this file stays free of the REST client, which is what lets
 * it be unit tested in isolation. Since rest-client 4.135.x the generated `MonitoringPromptRunListFilters`
 * declares this whole surface, so the two are the same shape by intent rather than by coincidence —
 * `GovernanceAnalyticsAiAnswers.utils.test.ts` asserts the assignability at compile time, so the day
 * the generated type moves and this one does not, the build says so instead of a cast hiding it.
 */
export interface AiAnswersRunListFilters {
  status?: MonitoringPromptRunStatus[];
  promptKey?: string;
  promptPurposeTermKey?: string;
  recommendedTermKey?: string;
  recommendedFacetKey?: string;
  aspectTermKey?: string;
  /**
   * NORMALIZED name of an untracked co-subject: runs whose `untrackedCoSubjects[]` mirror names it.
   * The server matches `norm` exactly, so a display spelling silently returns nothing.
   */
  untrackedCoSubjectNorm?: string;
  noWinner?: string;
  /**
   * Answers whose `citationHosts` mirror names this hostname. The server folds the input the same
   * way the display does, so a folded `example.com` matches the `www.example.com` the mirror
   * recorded verbatim.
   */
  citedHost?: string;
  /** Answers citing any hostname under this registrable domain — the grouped-view drill-down. */
  citedDomain?: string;
  /** Answers citing any hostname a curated `authority-source` term claims by name or alias. */
  citedTermKey?: string;
  /** CSV of engine taxonomy-term keys. */
  engineKey?: string;
  /** CSV of locale taxonomy-term keys. */
  localeKey?: string;
  /** CSV of facets: runs where such an entity is credited with shareOfVoice > 0. */
  mentionedFacetKey?: string;
  /** CSV of facets: runs where no such entity is credited. */
  notMentionedFacetKey?: string;
  /**
   * Locale term key to translate each run's prompt into. The server answers with
   * `promptTranslations` keyed by it, holding the sibling prompt describing the same content, or
   * nothing when it cannot resolve one unambiguously.
   */
  promptTranslationLocaleKey?: string;
}

/**
 * The facet CSVs the run-list endpoint's `recommendedFacetKey` takes for each side of the
 * "who won" question. They are derived from the very same facet lists `classifyEntityOwnership`
 * uses, so the server-side filter and the client-side winner classification can never disagree
 * about whether a run "recommends you".
 */
export const OWN_RECOMMENDED_FACET_KEYS = OWN_FACETS.join(',');
export const COMPETITOR_RECOMMENDED_FACET_KEYS = COMPETITOR_FACETS.join(',');

/**
 * The facet CSV the run-list endpoint's `mentionedFacetKey` / `notMentionedFacetKey` take for the
 * "was I visible in this answer?" question. Same list as the recommendation filter — visibility
 * and winning are different questions about the same set of own-side facets.
 */
export const OWN_MENTIONED_FACET_KEYS = OWN_FACETS.join(',');

/** The fields the list needs off a monitoring prompt to label a run's Prompt column. */
export interface PromptLabelSource {
  key?: string;
  name?: string;
  promptText?: string;
  /** Revision reference to the template this instance was generated from, when it is one. */
  templatePrompt?: unknown;
  /** Revision reference to the locale term this instance was generated for. */
  promptLocale?: unknown;
}

export interface PromptLabel {
  key: string;
  name: string;
  promptText: string;
}

/**
 * Indexes monitoring prompts by their stable key for the Prompt column join.
 *
 * Runs reference per-locale prompt **instances** (keys shaped
 * `project.<id>.<ts>.<slug>.instance.<cc>.<...>`), so the index has to be built from the instance
 * list — and from *every* instance, not just the currently active ones: a run keeps pointing at
 * the instance that produced it long after that instance is paused, archived or superseded.
 */
export function buildPromptLabelIndex(prompts: readonly PromptLabelSource[] | undefined): Map<string, PromptLabel> {
  const index = new Map<string, PromptLabel>();

  for (const prompt of prompts || []) {
    const key = String(prompt.key || '').trim();
    if (!key) {
      continue;
    }
    const promptText = String(prompt.promptText || '').trim();
    const name = promptText || String(prompt.name || '').trim();
    if (!name) {
      continue;
    }
    // First win: the list is fetched newest-status-first, and an already-indexed key means a
    // more relevant revision of the same prompt was seen already.
    if (!index.has(key)) {
      index.set(key, { key, name, promptText });
    }
  }

  return index;
}

/**
 * The human label for a run's prompt. Falls back to a neutral placeholder rather than the raw
 * key: a key like `project.664....instance.de.1` is noise to the reader, and showing it hides
 * the fact that the join missed.
 */
export function resolvePromptLabel(index: Map<string, PromptLabel>, promptKey: string): string {
  const entry = index.get(String(promptKey || '').trim());
  return entry?.promptText || entry?.name || UNKNOWN_PROMPT_LABEL;
}

/** Shown when the lookup genuinely finds no prompt — never a stand-in for a raw key. */
export const UNKNOWN_PROMPT_LABEL = 'Unknown prompt';

/**
 * The half of a run's `prompt` revision reference the label resolution needs.
 *
 * A run's `prompt` is a revision reference: a stable `key` naming the logical prompt plus an `id`
 * pinning the exact revision that produced the run. `IRuntimeRevisionReference` satisfies this
 * shape structurally, so a caller passes `run.prompt` unchanged.
 */
export interface PromptRevisionReference {
  /** The immutable id of the revision that produced the run. */
  getRevisionId(): string;
  /** Loads that exact revision by id. */
  getRevision(): Promise<PromptLabelSource | null | undefined>;
}

/** The revision id behind a run's prompt reference — the identity its label is cached under. */
export function promptRevisionId(reference: PromptRevisionReference): string {
  return String(reference.getRevisionId() || '').trim();
}

/** Which language the Prompt column shows. English-when-available is the default. */
export type PromptLanguageMode = 'english' | 'local';

/**
 * What a run's prompt can be displayed as.
 *
 * `english` comes from the SERVER. The run list and item accept a `promptTranslationLocaleKey`
 * and answer with `promptTranslations` keyed by that locale term key. Sync Core resolves the
 * sibling describing the same content — exact `templateTerm` match first, then its own text
 * fallback — and returns nothing when it cannot resolve unambiguously. The client no longer
 * guesses: it renders what the server resolved, or the unresolved marker. One resolver, one answer.
 */
export interface PromptDisplay {
  /** The run's own per-locale question text — always present, even if only as the fallback label. */
  local: string;
  /** The server-resolved translation for the requested locale, when it could resolve one. */
  english?: string;
}

/**
 * True when a locale identifies English.
 *
 * Matches the display name ("English", "English (US)") and the bare code forms (`en`, `en-US`,
 * `en_GB`), so it works whether the caller passes a term name or falls back to the term key.
 * Used to pick which of the project's locale terms to ask the server to translate into.
 */
export function isEnglishLocaleName(name?: string): boolean {
  const value = String(name || '').trim();
  if (!value) {
    return false;
  }
  return /\benglish\b/i.test(value) || /^en([-_][a-z0-9]+)*$/i.test(value);
}

/**
 * The English locale term key among the project's locale terms, or '' when it tracks none.
 * With no English locale there is nothing to translate into, so the flag is hidden entirely.
 */
export function findEnglishLocaleKey(terms: readonly { key?: string; name?: string }[] | undefined): string {
  for (const term of terms || []) {
    const key = String(term.key || '').trim();
    if (key && isEnglishLocaleName(term.name || key)) {
      return key;
    }
  }
  return '';
}

/**
 * The server-resolved translation text for one locale, or '' when the server resolved none.
 *
 * `promptTranslations` is a read-time map of locale term key -> the sibling prompt entity; the
 * shape is loose across the REST boundary, so it is read defensively.
 */
export function translationTextFor(promptTranslations: unknown, localeKey: string): string {
  if (!promptTranslations || typeof promptTranslations !== 'object' || !localeKey) {
    return '';
  }
  const sibling = (promptTranslations as Record<string, unknown>)[localeKey];
  if (!sibling || typeof sibling !== 'object') {
    return '';
  }
  const record = sibling as { promptText?: unknown; name?: unknown };
  return String(record.promptText || record.name || '').trim();
}

export interface PromptLabelResolver {
  /**
   * The run's own label per prompt revision id. A revision that resolves to nothing maps to
   * "Unknown prompt", never to a raw key.
   */
  resolve(references: readonly PromptRevisionReference[]): Promise<Map<string, string>>;
}

/** The label a prompt should carry: its question text, its admin name, or the unknown placeholder. */
export function promptLabelOf(prompt: PromptLabelSource | null | undefined): string {
  const promptText = String(prompt?.promptText || '').trim();
  const name = String(prompt?.name || '').trim();
  return promptText || name || UNKNOWN_PROMPT_LABEL;
}

/**
 * Resolves each run's OWN prompt label from the revision that produced it.
 *
 * Resolution goes through the reference's `getRevision()` — the by-id path — and deliberately NOT
 * through its `get()`. `get()` is a by-key lookup, and the client pins that lookup to the Active
 * status, so a run whose prompt has since been paused or archived resolves to nothing and its
 * label reads "Unknown prompt". Loading the revision by id carries no status filter, so it answers
 * for a prompt in any status.
 *
 * Reading the pinned revision is also the more correct answer for a screen showing a past answer:
 * it renders the question as it was asked when that answer was produced, rather than whatever the
 * prompt has been edited into since. Do not "simplify" this back to `get()`.
 *
 * Lookups are de-duplicated by revision id, run in parallel and cached across pages; failures are
 * not cached, so a transient error does not pin a row for the rest of the session.
 */
export function createPromptLabelResolver(): PromptLabelResolver {
  const promptCache = new Map<string, Promise<PromptLabelSource | null | undefined>>();

  const lookupPrompt = (revisionId: string, reference: PromptRevisionReference) => {
    const cached = promptCache.get(revisionId);
    if (cached) {
      return cached;
    }
    const pending = reference.getRevision().catch(() => {
      // A failed request is not evidence that the prompt is gone — let a later page retry.
      promptCache.delete(revisionId);
      return null;
    });
    promptCache.set(revisionId, pending);
    return pending;
  };

  return {
    async resolve(references) {
      // One entry per distinct revision: a page of runs repeats the same prompt many times over.
      const distinct = new Map<string, PromptRevisionReference>();
      for (const reference of references) {
        const revisionId = promptRevisionId(reference);
        if (revisionId && !distinct.has(revisionId)) {
          distinct.set(revisionId, reference);
        }
      }

      const entries = Array.from(distinct.entries());
      const prompts = await Promise.all(entries.map(([revisionId, reference]) => lookupPrompt(revisionId, reference)));

      const labels = new Map<string, string>();
      entries.forEach(([revisionId], index) => labels.set(revisionId, promptLabelOf(prompts[index])));
      return labels;
    },
  };
}

/**
 * The question text to render for a run.
 *
 * English is shown by default because a reader rarely speaks every brand language. When no English
 * text can be substituted the row keeps its own localized text — and the caller marks it, so with
 * the flag on a non-English row is never silently mixed in among the English ones.
 */

/** True when the row is showing its original language despite the English flag being on. */
export function shouldMarkOriginalLanguage(display: PromptDisplay | undefined, mode: PromptLanguageMode): boolean {
  return mode === 'english' && !display?.english;
}

/** Tooltip for the original-language marker. */
export const ORIGINAL_LANGUAGE_HINT = 'No English version of this question — showing original language';

export function pickPromptText(display: PromptDisplay | undefined, mode: PromptLanguageMode): string {
  if (!display) {
    return UNKNOWN_PROMPT_LABEL;
  }
  return mode === 'english' ? display.english || display.local : display.local;
}

function readParam(search: string, appQuery: unknown, key: string): string {
  const params = new URLSearchParams((search || '').replace(/^\?/, ''));
  const fromSearch = params.get(key);
  if (fromSearch && fromSearch.trim()) {
    return fromSearch.trim();
  }
  if (appQuery && typeof appQuery === 'object') {
    const value = (appQuery as Record<string, unknown>)[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

/** Parses the AI-answers filter state from the URL search string (and host query). */
export function parseAiAnswersFilterState(search: string, appQuery?: unknown): AiAnswersFilterState {
  const recommendation = readParam(search, appQuery, 'recommendation');
  const visibility = readParam(search, appQuery, 'visibility');
  const pageRaw = readParam(search, appQuery, 'page');
  const page = Number.parseInt(pageRaw, 10);

  return {
    purposeTermKey: readParam(search, appQuery, 'purpose'),
    engineKey: readParam(search, appQuery, 'engine'),
    localeKey: readParam(search, appQuery, 'locale'),
    promptKey: readParam(search, appQuery, 'prompt'),
    recommendation: (['all', 'us', 'competitor', 'none'].includes(recommendation) ? recommendation : 'all') as RecommendationFilter,
    visibility: (['all', 'visible', 'invisible'].includes(visibility) ? visibility : 'all') as VisibilityFilter,
    includeFailed: readParam(search, appQuery, 'includeFailed') === 'true',
    // Absent or anything unrecognised means the default: English when a sibling exists.
    promptLanguage: readParam(search, appQuery, 'promptLang') === 'local' ? 'local' : 'english',
    recommendedTermKey: readParam(search, appQuery, 'recommendedTerm'),
    aspectTermKey: readParam(search, appQuery, 'attribute'),
    coSubjectNorm: readParam(search, appQuery, 'coSubject'),
    // Folded on the way in so a link someone pasted with a `www.` prefix — or in mixed case —
    // selects the same chip the list would have highlighted for the bare host.
    citedHost: foldHost(readParam(search, appQuery, 'citedHost')),
    citedDomain: foldHost(readParam(search, appQuery, 'citedDomain')),
    citedTermKey: readParam(search, appQuery, 'citedTerm'),
    // Anything unrecognised means the default, so a mangled link still opens a working view.
    sourceGrouping: readParam(search, appQuery, 'sourceGrouping') === 'host' ? 'host' : DEFAULT_SOURCE_GROUPING,
    page: Number.isFinite(page) && page > 0 ? page : 0,
  };
}

/** Serializes the AI-answers filter state into a query object for update-query / links. */
export function serializeAiAnswersFilterState(state: AiAnswersFilterState): Record<string, string> {
  const query: Record<string, string> = {};
  if (state.purposeTermKey) query.purpose = state.purposeTermKey;
  if (state.engineKey) query.engine = state.engineKey;
  if (state.localeKey) query.locale = state.localeKey;
  if (state.promptKey) query.prompt = state.promptKey;
  if (state.recommendation !== 'all') query.recommendation = state.recommendation;
  if (state.visibility !== 'all') query.visibility = state.visibility;
  if (state.includeFailed) query.includeFailed = 'true';
  if (state.promptLanguage === 'local') query.promptLang = 'local';
  if (state.recommendedTermKey) query.recommendedTerm = state.recommendedTermKey;
  if (state.aspectTermKey) query.attribute = state.aspectTermKey;
  if (state.coSubjectNorm) query.coSubject = state.coSubjectNorm;
  if (state.citedHost) query.citedHost = state.citedHost;
  if (state.citedDomain) query.citedDomain = state.citedDomain;
  if (state.citedTermKey) query.citedTerm = state.citedTermKey;
  // Only when it differs from the default, so a shared link stays clean.
  if (state.sourceGrouping === 'host') query.sourceGrouping = 'host';
  if (state.page > 0) query.page = String(state.page);
  return query;
}

/** Statuses that count as a failed answer, for the "Include failed (N)" count. */
export const FAILED_STATUSES: MonitoringPromptRunStatus[] = [MonitoringPromptRunStatus.Failed];

/** Statuses whose analysis has not finished — nothing measured may be claimed for them. */
export function isPendingRunStatus(status?: MonitoringPromptRunStatus): boolean {
  return status === MonitoringPromptRunStatus.Queued || status === MonitoringPromptRunStatus.Fetched;
}

/** Only a processed run carries measurements; everything else is pending or failed. */
export function isProcessedRunStatus(status?: MonitoringPromptRunStatus): boolean {
  return status === MonitoringPromptRunStatus.Processed;
}

/**
 * The statuses the list shows by default: only answers whose analysis has finished.
 *
 * A queued or fetched run has no answer yet — no winner, no mentions, no sources — so its row is a
 * line of dashes and an "Analyzing…" chip taking up a slot in a list the reader is scanning for
 * findings. It is not hidden information: the run appears the moment it has something to say.
 *
 * Filtered SERVER-SIDE rather than dropped from the fetched page, so the pager counts stay true.
 * Removing rows after the fact would leave "Page 1 of 12" describing a set the reader cannot see,
 * and a page that renders three rows out of twenty-five.
 *
 * Exported so anything that COUNTS the same answers asks for the same set: a total taken over every
 * status names more answers than the list it opens can show.
 */
export const ANALYZED_STATUSES: MonitoringPromptRunStatus[] = [MonitoringPromptRunStatus.Processed];

/**
 * The same filters as the list, but restricted to failed answers — used to count them for the
 * "Include failed (N)" toggle, so the reader can tell whether anything is being hidden.
 */
export function buildFailedCountFilters(state: AiAnswersFilterState): AiAnswersRunListFilters {
  return { ...buildRunListFilters({ ...state, includeFailed: true }), status: FAILED_STATUSES };
}

/**
 * The filter-state keys that change only what is DRAWN, never what is asked of the server.
 *
 * Both readings they switch between are already in the fetched payload — the prompt's two languages
 * come down on every row, and the source chips regroup from the citation mirror the row carries. So
 * flipping one must not refetch and must not move the reader off their page. Treating them like
 * search filters is what made the language toggle throw the reader back to page one.
 *
 * A key belongs here only if `buildRunListFilters` ignores it; the round-trip test asserts exactly
 * that, so a key added to one list and not the other cannot pass silently.
 */
export const DISPLAY_ONLY_FILTER_KEYS: readonly (keyof AiAnswersFilterState)[] = ['promptLanguage', 'sourceGrouping'];

/**
 * Whether a filter patch only changes how the current results are drawn.
 *
 * An empty patch is not display-only: it asserts nothing, and defaulting it to "safe" would let a
 * future caller skip the page reset by accident.
 */
export function isDisplayOnlyChange(patch: Partial<AiAnswersFilterState>): boolean {
  const keys = Object.keys(patch) as (keyof AiAnswersFilterState)[];
  return keys.length > 0 && keys.every((key) => DISPLAY_ONLY_FILTER_KEYS.includes(key));
}

/**
 * Translates the UI filter state into the run-list endpoint filters.
 *
 * Every dimension the endpoint can express is resolved server-side, so the filters apply to the
 * whole result set rather than to the 25 rows that happened to be fetched. In particular the
 * recommendation tabs map onto `recommendedFacetKey` / `noWinner`; filtering them client-side
 * made "Recommends you" look empty whenever the current page held no winning own entity.
 */
export function buildRunListFilters(state: AiAnswersFilterState, englishLocaleKey?: string): AiAnswersRunListFilters {
  const filters: AiAnswersRunListFilters = {};

  if (state.promptKey) {
    filters.promptKey = state.promptKey;
  }

  if (state.purposeTermKey) {
    filters.promptPurposeTermKey = state.purposeTermKey;
  }

  // Asked for whenever the project tracks an English locale, INDEPENDENTLY of which language the
  // reader is currently looking at: both readings are cheap to carry, and the row needs both to
  // switch between them — `pickPromptText` picks one from what is already loaded, so the toggle
  // costs nothing but a repaint. Making the request depend on the display mode would turn the
  // language toggle into a server filter: flipping it would change the request, refetch the list,
  // and lose the reader's page.
  if (englishLocaleKey) {
    filters.promptTranslationLocaleKey = englishLocaleKey;
  }

  if (state.engineKey) {
    filters.engineKey = state.engineKey;
  }

  if (state.localeKey) {
    filters.localeKey = state.localeKey;
  }

  // Own-visibility is now a server-side question too: "visible" keeps runs crediting an own-side
  // entity with share of voice, "invisible" keeps runs crediting none.
  if (state.visibility === 'visible') {
    filters.mentionedFacetKey = OWN_MENTIONED_FACET_KEYS;
  } else if (state.visibility === 'invisible') {
    filters.notMentionedFacetKey = OWN_MENTIONED_FACET_KEYS;
  }

  // Answers still being analyzed are never listed; failed ones are hidden unless the operator opts
  // in. Spelled out as an explicit status list in BOTH cases rather than left unset, because an
  // unset status means "every status" — which is how opting into failed answers used to drag the
  // unanalyzed ones back in with them.
  filters.status = state.includeFailed ? [...ANALYZED_STATUSES, ...FAILED_STATUSES] : ANALYZED_STATUSES;

  if (state.recommendation === 'none') {
    filters.noWinner = 'true';
  } else if (state.recommendation === 'us') {
    // A deep link naming a specific own term is more precise than the facet family, so it wins.
    if (state.recommendedTermKey) {
      filters.recommendedTermKey = state.recommendedTermKey;
    } else {
      filters.recommendedFacetKey = OWN_RECOMMENDED_FACET_KEYS;
    }
  } else if (state.recommendation === 'competitor') {
    filters.recommendedFacetKey = COMPETITOR_RECOMMENDED_FACET_KEYS;
  } else if (state.recommendedTermKey) {
    filters.recommendedTermKey = state.recommendedTermKey;
  }

  if (state.coSubjectNorm) {
    filters.untrackedCoSubjectNorm = state.coSubjectNorm;
  }

  if (state.aspectTermKey) {
    filters.aspectTermKey = state.aspectTermKey;
  }

  // The three cited-source dimensions compose: a curated term names a publisher, and a host or
  // domain narrows within it. Each is sent only when it was actually asked for, so a link carrying
  // one never silently implies the others.
  if (state.citedTermKey) {
    filters.citedTermKey = state.citedTermKey;
  }

  if (state.citedDomain) {
    filters.citedDomain = state.citedDomain;
  }

  if (state.citedHost) {
    filters.citedHost = state.citedHost;
  }

  return filters;
}
